import { persianError } from "@car/api/error-message";
import type { AppRouter } from "@car/api/routers/index";
import { fieldLabel } from "@car/ui/lib/utils";
import { env } from "@car/env/web";
import { ORPCError, createORPCClient } from "@orpc/client";
import { RPCLink } from "@orpc/client/fetch";
import type { RouterClient } from "@orpc/server";
import { createTanstackQueryUtils } from "@orpc/tanstack-query";
import { MutationCache, QueryCache, QueryClient } from "@tanstack/react-query";
import { toast } from "sonner";

const isUnauthorized = (error: Error) =>
  error instanceof ORPCError && error.code === "UNAUTHORIZED";

/** متن خطا برای کاربر؛ نگاشت مشترک با پنل مدیریت */
export const userErrorMessage = persianError;

/** onUnauthorized: the session expired or was revoked; the router sends the user to login */
export function createQueryClient(onUnauthorized: () => void = () => {}) {
  return new QueryClient({
    queryCache: new QueryCache({
      onError: (error, query) => {
        // نشست منقضی یا باطل شده: روتر کاربر را به صفحه ورود می‌برد
        if (isUnauthorized(error)) return onUnauthorized();
        console.error(error);
        toast.error(persianError(error), {
          action: {
            label: "تلاش دوباره",
            onClick: () => {
              query.invalidate();
            },
          },
        });
      },
    }),
    mutationCache: new MutationCache({
      onError: (error, _variables, _result, mutation) => {
        if (isUnauthorized(error)) return onUnauthorized();
        // یک mutation با onError مخصوص خودش، شکست را خودش گزارش می‌کند
        if (mutation.options.onError) return;
        console.error(error);
        toast.error(`انجام نشد: ${persianError(error, fieldLabel)}`);
      },
    }),
    defaultOptions: { queries: { staleTime: 60 * 1000 } },
  });
}

function getServerUrl(url: string) {
  // SAFETY: process exists only in server runtimes; optional chain guards absence
  const processEnv = (
    globalThis as {
      process?: { env?: Record<string, string | undefined> };
    }
  ).process?.env;
  if (!("window" in globalThis) && processEnv?.SERVER_URL) {
    return processEnv.SERVER_URL.endsWith("/")
      ? processEnv.SERVER_URL.slice(0, -1)
      : processEnv.SERVER_URL;
  }

  const normalized = url.endsWith("/") ? url.slice(0, -1) : url;

  if (!normalized.startsWith("/")) {
    return normalized;
  }

  if ("window" in globalThis) {
    return `${window.location.origin}${normalized}`;
  }

  const vercelUrl =
    processEnv?.VERCEL_ENV === "production"
      ? (processEnv?.VERCEL_PROJECT_PRODUCTION_URL ?? processEnv?.VERCEL_URL)
      : (processEnv?.VERCEL_URL ?? processEnv?.VERCEL_PROJECT_PRODUCTION_URL);
  if (vercelUrl) {
    const origin = vercelUrl.startsWith("http") ? vercelUrl : `https://${vercelUrl}`;
    return `${origin}${normalized}`;
  }

  return `http://localhost:3000${normalized}`;
}
const link = new RPCLink({
  url: `${getServerUrl(env.VITE_SERVER_URL)}/rpc`,
  fetch(url, options) {
    return fetch(url, {
      ...options,
      credentials: "include",
    });
  },
});

const getORPCClient = () => {
  // SAFETY: link points at the AppRouter RPC endpoint, so the client matches
  return createORPCClient(link) as RouterClient<AppRouter>;
};

export const client: RouterClient<AppRouter> = getORPCClient();

export const orpc = createTanstackQueryUtils(client);
